const SEO_META_PREFIX = 'data-seo';
const SEO_ORIG_ATTR = 'data-seo-orig';

function setMeta(name, content, property = false) {
    const attr = property ? 'property' : 'name';
    // Update the static tag from index.html in place when one exists (avoids
    // shipping duplicate <meta> tags), remembering the original value so
    // clearProductSeo() can restore it on route change.
    const existing = document.querySelector(`meta[${attr}="${name}"]:not([${SEO_META_PREFIX}])`);
    if (existing) {
        if (!existing.hasAttribute(SEO_ORIG_ATTR)) {
            existing.setAttribute(SEO_ORIG_ATTR, existing.getAttribute('content') || '');
        }
        existing.setAttribute('content', content);
        return;
    }
    let el = document.querySelector(`meta[${attr}="${name}"][${SEO_META_PREFIX}]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        el.setAttribute(SEO_META_PREFIX, '');
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}

function setJsonLd(data) {
    let el = document.querySelector(`script[${SEO_META_PREFIX}]`);
    if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute(SEO_META_PREFIX, '');
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

export function setCanonical(href) {
    // Keep exactly ONE canonical. Reuse the static one from index.html and only
    // ever update its href — never tag it with data-seo, so clearProductSeo()
    // can't delete it and leave a route with no canonical.
    let el = document.querySelector('link[rel="canonical"]');
    if (!el) {
        el = document.createElement('link');
        el.rel = 'canonical';
        document.head.appendChild(el);
    }
    el.href = href;
}

function setJsonLdKey(key, data) {
    const selector = `script[${SEO_META_PREFIX}-${key}]`;
    let el = document.querySelector(selector);
    if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute(SEO_META_PREFIX, '');
        el.setAttribute(`${SEO_META_PREFIX}-${key}`, '');
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

export function clearProductSeo() {
    document.querySelectorAll(`[${SEO_META_PREFIX}]`).forEach(el => el.remove());
    document.querySelectorAll(`[${SEO_ORIG_ATTR}]`).forEach(el => {
        el.setAttribute('content', el.getAttribute(SEO_ORIG_ATTR) || '');
        el.removeAttribute(SEO_ORIG_ATTR);
    });
}

const INDEXABLE = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

/**
 * Toggle the page's robots directive. The site is a SPA, so a "not found"
 * route still returns HTTP 200 (the app shell) — which Google reports as a
 * "Soft 404". Marking those pages noindex tells Google not to index them.
 */
export function setRobots(content) {
    let el = document.querySelector('meta[name="robots"]');
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', 'robots');
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}
export const setNoIndex = () => setRobots('noindex, follow');
export const setIndexable = () => setRobots(INDEXABLE);

const SITE_NAME = 'Jungle House Dispensary';
const SITE_ORIGIN = 'https://junglehouse.co.th';
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/img/og-share.png`;

function truncate(str, n = 160) {
    if (!str) return '';
    return str.length > n ? str.slice(0, n - 3) + '...' : str;
}

/**
 * SEO for the /blog index page.
 */
export function setBlogIndexSeo(posts = []) {
    const title = `Cannabis Blog – Strain Guides & Phuket Weed Tips | ${SITE_NAME}`;
    const desc = 'Cannabis guides for Phuket: strain reviews, Thailand weed laws, and tips for visitors to Rawai, Nai Harn, Kata & Karon. From Jungle House Dispensary.';
    const url = `${SITE_ORIGIN}/blog`;

    document.title = title;
    setMeta('description', desc);
    setMeta('og:title', title, true);
    setMeta('og:description', desc, true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', url, true);
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:image', DEFAULT_OG_IMAGE, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', desc);
    setMeta('twitter:image', DEFAULT_OG_IMAGE);
    setCanonical(url);

    setJsonLdKey('blog', {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: `${SITE_NAME} Blog`,
        description: desc,
        url,
        publisher: {
            '@type': 'Organization',
            name: SITE_NAME,
            url: `${SITE_ORIGIN}/`,
            logo: { '@type': 'ImageObject', url: DEFAULT_OG_IMAGE }
        },
        blogPost: posts.map(p => ({
            '@type': 'BlogPosting',
            headline: p.title,
            url: `${SITE_ORIGIN}/blog/${p.slug}`,
            datePublished: p.date,
            dateModified: p.updated || p.date,
            image: p.cover ? absUrl(p.cover) : DEFAULT_OG_IMAGE
        }))
    });
}

function absUrl(path) {
    if (!path) return DEFAULT_OG_IMAGE;
    return path.startsWith('http') ? path : `${SITE_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;
}

/**
 * SEO for the /about page.
 */
export function setAboutSeo() {
    const title = `About Jungle House | Rawai's Trusted Cannabis Dispensary, Phuket`;
    const desc = 'Jungle House is Rawai\'s trusted cannabis dispensary, open since 2023. Premium flower at honest prices, expert guidance, and full Thai compliance in South Phuket.';
    const url = `${SITE_ORIGIN}/about`;

    document.title = title;
    setMeta('description', desc);
    setMeta('og:title', title, true);
    setMeta('og:description', desc, true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', url, true);
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:image', DEFAULT_OG_IMAGE, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', desc);
    setMeta('twitter:image', DEFAULT_OG_IMAGE);
    setCanonical(url);

    setJsonLdKey('about', {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        url,
        mainEntity: {
            '@type': 'Organization',
            name: SITE_NAME,
            url: `${SITE_ORIGIN}/`,
            logo: { '@type': 'ImageObject', url: DEFAULT_OG_IMAGE },
            description: desc,
            foundingDate: '2023-11',
            address: {
                '@type': 'PostalAddress',
                addressLocality: 'Rawai',
                addressRegion: 'Phuket',
                postalCode: '83130',
                addressCountry: 'TH'
            }
        }
    });
}

/**
 * SEO for the /find-us page.
 */
export function setFindUsSeo() {
    const title = `How to Find Us | Jungle House Dispensary, Rawai, Phuket`;
    const desc = 'Find Jungle House Dispensary at 87/77 Soi Sayuan, Rawai, Phuket. Map, directions and opening hours. Open daily 11AM–11PM.';
    const url = `${SITE_ORIGIN}/find-us`;

    document.title = title;
    setMeta('description', desc);
    setMeta('og:title', title, true);
    setMeta('og:description', desc, true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', url, true);
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:image', DEFAULT_OG_IMAGE, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', desc);
    setMeta('twitter:image', DEFAULT_OG_IMAGE);
    setCanonical(url);

    setJsonLdKey('findus', {
        '@context': 'https://schema.org',
        '@type': 'CannabisStore',
        name: SITE_NAME,
        url: `${SITE_ORIGIN}/`,
        image: DEFAULT_OG_IMAGE,
        telephone: '+66914200007',
        address: {
            '@type': 'PostalAddress',
            streetAddress: '87/77 Soi Sayuan',
            addressLocality: 'Rawai',
            addressRegion: 'Phuket',
            postalCode: '83130',
            addressCountry: 'TH'
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: 7.7972176,
            longitude: 98.3273354
        },
        hasMap: 'https://maps.app.goo.gl/PdFtnPer9jmCi8kh7',
        openingHoursSpecification: [{
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
            opens: '11:00',
            closes: '23:00'
        }]
    });
}

/**
 * SEO for a single /blog/:slug post. Sets meta tags, BlogPosting schema,
 * a BreadcrumbList, and (optionally) an FAQPage from post.faq.
 */
export function setArticleSeo(post) {
    if (!post) return;
    const url = `${SITE_ORIGIN}/blog/${post.slug}`;
    const title = `${post.title} | ${SITE_NAME}`;
    const desc = truncate(post.description);
    const image = absUrl(post.cover);

    document.title = title;
    setMeta('description', desc);
    setMeta('og:title', post.title, true);
    setMeta('og:description', desc, true);
    setMeta('og:type', 'article', true);
    setMeta('og:url', url, true);
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:image', image, true);
    setMeta('og:image:alt', post.coverAlt || post.title, true);
    setMeta('article:published_time', post.date, true);
    setMeta('article:modified_time', post.updated || post.date, true);
    (post.tags || []).forEach(tag => {
        const el = document.createElement('meta');
        el.setAttribute('property', 'article:tag');
        el.setAttribute(SEO_META_PREFIX, '');
        el.setAttribute('content', tag);
        document.head.appendChild(el);
    });
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', post.title);
    setMeta('twitter:description', desc);
    setMeta('twitter:image', image);
    setCanonical(url);

    setJsonLdKey('article', {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.description,
        image: [image],
        datePublished: post.date,
        dateModified: post.updated || post.date,
        url,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        keywords: (post.tags || []).join(', '),
        author: {
            '@type': 'Organization',
            name: post.author || SITE_NAME,
            url: `${SITE_ORIGIN}/`
        },
        publisher: {
            '@type': 'Organization',
            name: SITE_NAME,
            url: `${SITE_ORIGIN}/`,
            logo: { '@type': 'ImageObject', url: DEFAULT_OG_IMAGE }
        }
    });

    setJsonLdKey('breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_ORIGIN}/blog` },
            { '@type': 'ListItem', position: 3, name: post.title, item: url }
        ]
    });

    if (post.faq && post.faq.length) {
        setJsonLdKey('faq', {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: post.faq.map(f => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a }
            }))
        });
    }
}

/**
 * Shared local-business + breadcrumb + FAQ schema for a keyword landing page.
 */
function setLandingSeo({ slug, title, desc, breadcrumbName, faq, areaServed }) {
    const url = `${SITE_ORIGIN}/${slug}`;

    document.title = title;
    setMeta('description', desc);
    setMeta('og:title', title, true);
    setMeta('og:description', desc, true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', url, true);
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:image', DEFAULT_OG_IMAGE, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', desc);
    setMeta('twitter:image', DEFAULT_OG_IMAGE);
    setCanonical(url);

    setJsonLdKey('landing', {
        '@context': 'https://schema.org',
        '@type': 'CannabisStore',
        name: SITE_NAME,
        description: desc,
        url,
        image: DEFAULT_OG_IMAGE,
        telephone: '+66914200007',
        priceRange: '$$',
        currenciesAccepted: 'THB',
        paymentAccepted: 'Cash, Credit Card, QR Payment, Bank Transfer',
        address: {
            '@type': 'PostalAddress',
            streetAddress: '87/77 Soi Sayuan',
            addressLocality: 'Rawai',
            addressRegion: 'Phuket',
            postalCode: '83130',
            addressCountry: 'TH'
        },
        geo: { '@type': 'GeoCoordinates', latitude: 7.7972176, longitude: 98.3273354 },
        hasMap: 'https://maps.app.goo.gl/PdFtnPer9jmCi8kh7',
        areaServed: (areaServed || ['Rawai', 'Nai Harn', 'Chalong', 'Kata', 'Karon']).map(n => ({
            '@type': 'City', name: `${n}, Phuket`
        })),
        openingHoursSpecification: [{
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
            opens: '11:00',
            closes: '23:00'
        }]
    });

    setJsonLdKey('breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
            { '@type': 'ListItem', position: 2, name: breadcrumbName, item: url }
        ]
    });

    if (faq && faq.length) {
        setJsonLdKey('faq', {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faq.map(f => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a }
            }))
        });
    }
}

export function setWeedDeliveryRawaiSeo(faq) {
    setLandingSeo({
        slug: 'weed-delivery-rawai',
        title: `Weed Delivery Rawai | Fast Cannabis Delivery in South Phuket – ${SITE_NAME}`,
        desc: 'Weed delivery in Rawai, Phuket from Jungle House Dispensary. Order premium cannabis flower, pre-rolls & edibles on WhatsApp — fast delivery to Rawai, Nai Harn, Chalong & Kata. Open daily 11AM–11PM.',
        breadcrumbName: 'Weed Delivery Rawai',
        faq
    });
}

export function setWeedDeliveryPhuketSeo(faq) {
    setLandingSeo({
        slug: 'weed-delivery-phuket',
        title: `Weed Delivery Phuket | Island-Wide Cannabis Delivery – ${SITE_NAME}`,
        desc: 'Weed delivery anywhere in Phuket from Jungle House Dispensary. Order premium cannabis flower, pre-rolls & edibles on WhatsApp — delivery to Rawai, Chalong, Kata, Karon, Phuket Town & Patong. Open daily 11AM–11PM.',
        breadcrumbName: 'Weed Delivery Phuket',
        faq,
        areaServed: ['Rawai', 'Nai Harn', 'Chalong', 'Kata', 'Karon', 'Phuket Town', 'Patong', 'Kamala', 'Bang Tao']
    });
}

export function setWeedShopRawaiSeo(faq) {
    setLandingSeo({
        slug: 'weed-shop-rawai',
        title: `Weed Shop Rawai | Best Cannabis Dispensary in Rawai, Phuket – ${SITE_NAME}`,
        desc: 'Looking for a weed shop in Rawai? Jungle House Dispensary is Rawai\'s top-rated cannabis dispensary — premium flower, pre-rolls, edibles & accessories at honest prices. Walk in or order delivery. Open daily 11AM–11PM.',
        breadcrumbName: 'Weed Shop Rawai',
        faq
    });
}

/**
 * SEO for the /products menu page. Re-run when the category changes so each
 * category gets its own title, canonical and ItemList structured data.
 */
export function setMenuSeo(category, items = []) {
    const cat = category || 'Flower';
    const isFlower = cat.toLowerCase() === 'flower';
    const url = `${SITE_ORIGIN}/products?category=${encodeURIComponent(cat)}`;

    const title = isFlower
        ? `Cannabis Flower Menu – Live Strains & Prices | ${SITE_NAME} Rawai`
        : `${cat} – Shop Online in Rawai, Phuket | ${SITE_NAME}`;
    const desc = isFlower
        ? 'Live cannabis flower menu at Jungle House Rawai — indica, sativa & hybrid strains with real photos, THC info and honest prices. Delivery across Phuket in under 90 minutes.'
        : `Shop ${cat.toLowerCase()} at Jungle House Dispensary in Rawai, Phuket. Real photos, live stock and honest prices — fast delivery across South Phuket or pick up in store.`;

    document.title = title;
    setIndexable();
    setMeta('description', truncate(desc));
    setMeta('og:title', title, true);
    setMeta('og:description', truncate(desc), true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', url, true);
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:image', DEFAULT_OG_IMAGE, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', truncate(desc));
    setMeta('twitter:image', DEFAULT_OG_IMAGE);
    setCanonical(url);

    const listItems = (items || []).slice(0, 24).map((item, i) => {
        const raw = item._raw || item;
        const isFlowerItem = item.itemType === 'flower' || raw.strain;
        const typeParam = isFlowerItem ? '' : '&type=inventory';
        return {
            '@type': 'ListItem',
            position: i + 1,
            url: `${SITE_ORIGIN}/product/${raw.id}?category=${encodeURIComponent(cat)}${typeParam}`,
            name: item.title || raw.strain || raw.name || ''
        };
    });

    if (listItems.length) {
        setJsonLdKey('menu', {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: isFlower ? 'Cannabis Flower Menu' : `${cat} Menu`,
            numberOfItems: listItems.length,
            itemListElement: listItems
        });
    }

    setJsonLdKey('breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
            { '@type': 'ListItem', position: 2, name: 'Menu', item: `${SITE_ORIGIN}/products` },
            { '@type': 'ListItem', position: 3, name: cat, item: url }
        ]
    });
}

export function setProductSeo({ name, description, price, currency, image, url, category, inStock, brand, reviews }) {
    const siteName = 'Jungle House Dispensary';
    const title = `${name} | ${siteName} – Rawai, Phuket`;
    document.title = title;

    const desc = description
        ? `${name} – ${description.slice(0, 120)}. Buy at Jungle House Dispensary, Rawai's best weed shop in Phuket. Delivery available.`
        : `Buy ${name} at Jungle House Dispensary in Rawai, Phuket. Premium cannabis products with delivery in Rawai & South Phuket. Open daily.`;

    const truncatedDesc = desc.length > 160 ? desc.slice(0, 157) + '...' : desc;

    setMeta('description', truncatedDesc);
    setMeta('og:title', title, true);
    setMeta('og:description', truncatedDesc, true);
    setMeta('og:type', 'product', true);
    setMeta('og:url', url, true);
    setMeta('og:site_name', siteName, true);

    // Always provide an image. A missing product image makes the Product
    // structured data invalid (ineligible for rich results), so fall back to
    // the branded image when a product has no photo of its own.
    const productImage = image || DEFAULT_OG_IMAGE;
    setMeta('og:image', productImage, true);
    setMeta('og:image:alt', `${name} – ${siteName}`, true);
    setMeta('twitter:image', productImage);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', truncatedDesc);

    setCanonical(url);

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name,
        description: description || `${name} available at Jungle House Dispensary in Rawai, Phuket`,
        image: productImage,
        url,
        brand: {
            '@type': 'Brand',
            name: brand || siteName
        },
        category: category || 'Cannabis Products',
        offers: {
            '@type': 'Offer',
            price: price || undefined,
            priceCurrency: currency || 'THB',
            availability: inStock !== false
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock',
            seller: {
                '@type': 'Organization',
                name: siteName,
                url: 'https://junglehouse.co.th/'
            },
            itemCondition: 'https://schema.org/NewCondition'
        }
    };

    // Only emit aggregateRating / review when this product has REAL approved
    // reviews. Never fabricate ratings — fake structured data risks a manual
    // penalty and is worse than the "missing field" warning.
    if (Array.isArray(reviews) && reviews.length) {
        const count = reviews.length;
        const sum = reviews.reduce((acc, r) => acc + Number(r.rating || 0), 0);
        const average = sum / count;

        jsonLd.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: average.toFixed(1),
            reviewCount: String(count),
            bestRating: '5',
            worstRating: '1'
        };

        jsonLd.review = reviews.slice(0, 20).map(r => ({
            '@type': 'Review',
            reviewRating: {
                '@type': 'Rating',
                ratingValue: String(r.rating),
                bestRating: '5',
                worstRating: '1'
            },
            author: { '@type': 'Person', name: r.author || 'Verified customer' },
            datePublished: r.created ? String(r.created).slice(0, 10) : undefined,
            reviewBody: r.comment || undefined
        }));
    }

    setJsonLd(jsonLd);

    // Breadcrumbs help Google show "Home › Menu › Product" trails in results.
    const crumbCat = (category || '').startsWith('Cannabis Flower') ? 'Flower' : (category || 'All');
    setJsonLdKey('breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
            { '@type': 'ListItem', position: 2, name: crumbCat, item: `${SITE_ORIGIN}/products?category=${encodeURIComponent(crumbCat)}` },
            { '@type': 'ListItem', position: 3, name, item: url }
        ]
    });
}
